
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EventData } from "@/types/guest";
import { useToast } from "@/hooks/use-toast";
import { datesArrayToStrings } from "@/utils/dateUtils";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { captureException } from "@/integrations/sentry/client";

export const useCreateDraftEvent = (
  onSuccess: (eventId: string) => void,
  ensureAuthenticatedUser: () => Promise<string>
) => {
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: async ({ eventData, selectedGuests, initialGuests }: { 
      eventData: EventData; 
      selectedGuests: string[]; 
      initialGuests?: Array<{ firstName: string; lastName: string }>;
    }) => {
      try {
        const userId = await ensureAuthenticatedUser();

        // Format preferred dates using our utility function to preserve local date
        const formattedDates = eventData.preferredDates?.length 
          ? datesArrayToStrings(eventData.preferredDates)
          : [];

        console.log('Creating event with dates:', formattedDates);
        console.log('Creating event with venues:', eventData.preferredVenues);
        console.log('Creating event with deadline:', eventData.event_deadline);

        // Ensure title is never empty
        const eventTitle = eventData.title?.trim() || "Untitled Event";

        // Create event first
        const { data: event, error: eventError } = await supabase
          .from('events')
          .insert({
            name: eventTitle,
            description: eventData.description,
            event_date: eventData.date?.toISOString(),
            location: eventData.location,
            flexible_location_description: eventData.flexible_location_description,
            status: 'planned',
            host_user_id: userId,
            image_url: eventData.imageUrl,
            preferred_time_of_day: eventData.preferredTimeOfDay,
            preferred_dates: formattedDates,
            preferred_venues: eventData.preferredVenues || [],
            location_type: eventData.locationType || 'specific',
            event_deadline: eventData.event_deadline?.toISOString() || null
          })
          .select()
          .single();

        if (eventError) {
          // Track error with Sentry
          captureException(eventError, {
            eventData: {
              title: eventTitle,
              preferredDates: formattedDates,
              preferredTimeOfDay: eventData.preferredTimeOfDay,
              locationType: eventData.locationType
            }
          });
          
          console.error('Error creating event:', eventError);
          throw eventError;
        }
        
        // Handle images - save to event_images table
        if (eventData.imageUrl || (eventData.additionalImages && eventData.additionalImages.length > 0)) {
          // Save all images to event_images table
          const allImageUrls = [
            ...(eventData.imageUrl ? [eventData.imageUrl] : []),
            ...(eventData.additionalImages || [])
          ];
          
          // Create image entries
          for (let i = 0; i < allImageUrls.length; i++) {
            const imageUrl = allImageUrls[i];
            const isPrimary = i === 0; // First image is primary
            
            const { error: imageError } = await supabase
              .from('event_images')
              .insert({
                event_id: event.event_id,
                image_url: imageUrl,
                is_primary: isPrimary,
                display_order: i
              });
              
            if (imageError) {
              // Track error with Sentry
              captureException(imageError, {
                context: 'event_image_creation',
                eventId: event.event_id,
                imageIndex: i
              });
              
              console.error('Error saving image to event_images:', imageError);
            }
          }
        }

        // Handle selected guests (existing users)
        if (selectedGuests.length > 0) {
          const attendeeRecords = selectedGuests.map(guestId => ({
            event_id: event.event_id,
            user_id: guestId,
            rsvp_status: 'invited' as const
          }));

          const { error: attendeeError } = await supabase
            .from('event_attendee')
            .insert(attendeeRecords);

          if (attendeeError) {
            // Track error with Sentry
            captureException(attendeeError, {
              context: 'event_attendee_creation',
              eventId: event.event_id,
              attendeeCount: selectedGuests.length
            });
            
            throw attendeeError;
          }
        }

        // Handle initial guest invites
        if (initialGuests && initialGuests.length > 0) {
          console.log('Creating invites for initial guests:', initialGuests);
          
          const inviteRecords = initialGuests.map(guest => ({
            event_id: event.event_id,
            display_name: `${guest.firstName} ${guest.lastName}`.trim() || guest.firstName,
            rsvp_status: 'invited' as const
          }));

          const { error: inviteError } = await supabase
            .from('event_invites')
            .insert(inviteRecords);

          if (inviteError) {
            // Track error with Sentry
            captureException(inviteError, {
              context: 'event_invites_creation',
              eventId: event.event_id,
              inviteCount: initialGuests.length
            });
            
            console.error('Error creating invites:', inviteError);
            throw inviteError;
          }
        }

        return event;
      } catch (error) {
        // Track the overall error
        handleError(error, {
          context: {
            operation: 'create_draft_event',
            eventTitle: eventData.title,
            guestCount: (selectedGuests?.length || 0) + (initialGuests?.length || 0)
          },
          toastMessage: "Failed to save event. Please try again."
        });
        throw error;
      }
    },
    onSuccess: (event) => {
      toast({
        title: "Success",
        description: "Event created successfully",
      });
      onSuccess(event.event_id);
    },
    onError: (error: any) => {
      console.error('Error saving draft:', error);
      toast({
        title: "Error",
        description: "Failed to save draft. Please try again.",
        variant: "destructive",
      });
    }
  });
};
